import React from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DeclinePayload } from 'src/Ghg/interfaces';

const T_PREFIX = 'ghg.declineReason';
const OTHER_REASON = 'other';
// Same limit as the backend (DECLINE_REASON_TEXT_MAX_LENGTH).
const REASON_TEXT_MAX_LENGTH = 500;

interface Props {
  // Which decision is being declined. The codes themselves come from the
  // server (`options`), so this only labels the form for tests/styling.
  context: 'letter' | 'target';
  // Ordered reason codes from the server (`decline_reason_options`). Missing
  // or empty -> no Select, and confirming sends `reason: null`.
  options?: string[];
  loading?: boolean;
  // eslint-disable-next-line no-unused-vars
  onConfirm: (payload: DeclinePayload) => void;
  onCancel: () => void;
}

// RG 28.08 1.3/8.4, D3: the optional "why are you declining" step shown after
// "Odrzucam" (Letter pop-up, program tab, target pop-up). Purely
// presentational and inline (no Dialog of its own, so it works inside a
// pop-up as well as on the page): it owns the form state and hands the
// result to `onConfirm`; the caller does the POST.
export default function DeclineReasonForm({
  context,
  options,
  loading = false,
  onConfirm,
  onCancel,
}: Props) {
  const { t } = useTranslation(undefined, { keyPrefix: T_PREFIX });
  const [reason, setReason] = React.useState('');
  const [reasonText, setReasonText] = React.useState('');

  const hasOptions = !!options && options.length > 0;

  const handleReasonChange = (event: SelectChangeEvent<string>) => {
    setReason(event.target.value);
  };

  const handleConfirm = () => {
    const code = hasOptions && reason !== '' ? reason : null;
    onConfirm({
      reason: code,
      // The text only belongs to the free-form "other" reason.
      reasonText: code === OTHER_REASON ? reasonText : '',
    });
  };

  return (
    <Box
      role="group"
      aria-label={t('title')}
      data-testid="decline-reason-form"
      data-context={context}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}
    >
      <Typography variant="subtitle1">{t('title')}</Typography>

      {hasOptions && (
        <FormControl size="small" fullWidth>
          <InputLabel id="decline-reason-select-label" shrink>
            {t('selectLabel')}
          </InputLabel>
          <Select<string>
            labelId="decline-reason-select-label"
            label={t('selectLabel')}
            value={reason}
            displayEmpty
            notched
            onChange={handleReasonChange}
          >
            <MenuItem value="">{t('noReason')}</MenuItem>
            {options.map((code) => (
              <MenuItem key={code} value={code}>
                {t(`codes.${code}`, { defaultValue: code })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {hasOptions && reason === OTHER_REASON && (
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={2}
          label={t('otherTextLabel')}
          value={reasonText}
          onChange={(event) =>
            setReasonText(event.target.value.slice(0, REASON_TEXT_MAX_LENGTH))
          }
          inputProps={{ maxLength: REASON_TEXT_MAX_LENGTH }}
        />
      )}

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button variant="text" onClick={onCancel} disabled={loading}>
          {t('back')}
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={loading}>
          {t('confirm')}
        </Button>
      </Box>
    </Box>
  );
}
